import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import supportApiService from '../../services/supportApiService';
import authService from '../../utils/authService';

const SupportDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [showPassword, setShowPassword] = useState({
    newPassword: false,
    confirmPassword: false
  });

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      console.log('🔍 Loading Support Dashboard data...');
      const data = await supportApiService.getDashboard();
      console.log('📊 Support Dashboard API Response:', data);
      
      if (data.success) {
        setDashboardData(data);
        console.log('✅ Dashboard data set successfully');
        console.log('📈 Tickets Stats:', data.ticketsStats);
        console.log('👥 Approval Stats:', data.approvalStatsDto);
        console.log('📅 Today\'s Report:', data.todaysReport);
        console.log('📊 Monthly Report:', data.monthlyReport);
        
        // Check if password change is required - matching Thymeleaf template
        if (data.showChangePasswordModal) {
          console.log('🔐 Password change required');
          setShowPasswordModal(true);
        }
      } else {
        console.error('❌ Dashboard API returned error:', data.message);
        toast.error(data.message || 'Failed to load dashboard');
      }
    } catch (error) {
      console.error('❌ Dashboard load error:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = (field, value) => {
    setPasswordForm(prev => ({ ...prev, [field]: value }));
    setPasswordError('');
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    const { newPassword, confirmPassword } = passwordForm;
    
    // Validation
    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters long.');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match.');
      return;
    }
    
    // Strong password validation
    const regex = /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*]).+$/;
    if (!regex.test(newPassword)) {
      setPasswordError('Password must include an uppercase letter, number, and special character.');
      return;
    }
    
    try {
      // Here you would call the password update API
      // await supportApiService.updatePassword(newPassword);
      // Removed success toast - only show errors
      setShowPasswordModal(false);
      setPasswordForm({ newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error('Failed to update password');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">Failed to load dashboard data</p>
        <button 
          onClick={loadDashboardData}
          className="mt-4 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md"
        >
          Retry
        </button>
      </div>
    );
  }

  const { ticketsStats, approvalStatsDto, resolvedStats, todaysReport, monthlyReport } = dashboardData;

  // Debug logging to console
  console.log('🎯 Current dashboard data:', {
    ticketsStats,
    approvalStatsDto,
    resolvedStats,
    todaysReport,
    monthlyReport
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Support Dashboard</h1>
          <p className="text-gray-600">Welcome back, {dashboardData.authenticatedUser}</p>
        </div>



        {/* Stats Cards Row 1 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Total Open Tickets */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600 mb-1">Total Open Tickets</p>
                <p className="text-2xl font-bold text-gray-900">
                  {ticketsStats?.totalOpenTickets !== undefined ? ticketsStats.totalOpenTickets : 'Loading...'}
                </p>
                {ticketsStats?.totalOpenTickets === 0 && (
                  <p className="text-xs text-gray-500">No open tickets</p>
                )}
              </div>
              <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
                <i className="fas fa-ticket-alt text-blue-600 text-xl"></i>
              </div>
            </div>
          </div>

          {/* Current Assigned Tickets */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">Current Assigned Tickets</p>
                <p className="text-2xl font-bold text-gray-900">
                  {ticketsStats?.totalAssignedTickets !== undefined ? ticketsStats.totalAssignedTickets : 'Loading...'}
                </p>
                {ticketsStats?.totalAssignedTickets === 0 && (
                  <p className="text-xs text-gray-500">No assigned tickets</p>
                )}
              </div>
              <div className="w-12 h-12 bg-gray-50 rounded-lg flex items-center justify-center">
                <i className="fas fa-tag text-gray-600 text-xl"></i>
              </div>
            </div>
          </div>

          {/* Current InProgress Tickets */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600 mb-1">Current InProgress Tickets</p>
                <p className="text-2xl font-bold text-gray-900">
                  {ticketsStats?.totalInProgressTickets !== undefined ? ticketsStats.totalInProgressTickets : 'Loading...'}
                </p>
                {ticketsStats?.totalInProgressTickets === 0 && (
                  <p className="text-xs text-gray-500">No in-progress tickets</p>
                )}
              </div>
              <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center">
                <i className="fas fa-spinner text-green-600 text-xl"></i>
              </div>
            </div>
          </div>

          {/* Total Resolved Tickets */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-cyan-600 mb-1">Total Resolved Tickets</p>
                <p className="text-2xl font-bold text-gray-900">
                  {ticketsStats?.totalResolveTickets !== undefined ? ticketsStats.totalResolveTickets : 'Loading...'}
                </p>
                {ticketsStats?.totalResolveTickets === 0 && (
                  <p className="text-xs text-gray-500">No resolved tickets</p>
                )}
              </div>
              <div className="w-12 h-12 bg-cyan-50 rounded-lg flex items-center justify-center">
                <i className="fas fa-check-circle text-cyan-600 text-xl"></i>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards Row 2 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Current Month Resolved Tickets */}
          <div className="bg-blue-600 text-white rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm mb-1">Current Month Resolved Tickets</p>
                <p className="text-2xl font-bold">
                  {resolvedStats?.currentMonthResolvedTickets !== undefined ? resolvedStats.currentMonthResolvedTickets : 'Loading...'}
                </p>
                {resolvedStats?.currentMonthResolvedTickets === 0 && (
                  <p className="text-xs text-blue-200">No resolved tickets this month</p>
                )}
              </div>
              <i className="fas fa-calendar text-blue-200 text-2xl"></i>
            </div>
          </div>

          {/* Last 12 Months Resolved Tickets */}
          <div className="bg-yellow-500 text-white rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-yellow-100 text-sm mb-1">Last 12 months Resolved Tickets</p>
                <p className="text-2xl font-bold">
                  {resolvedStats?.last12MonthsResolvedTickets !== undefined ? resolvedStats.last12MonthsResolvedTickets : 'Loading...'}
                </p>
                {resolvedStats?.last12MonthsResolvedTickets === 0 && (
                  <p className="text-xs text-yellow-200">No resolved tickets in last 12 months</p>
                )}
              </div>
              <i className="fas fa-chart-line text-yellow-200 text-2xl"></i>
            </div>
          </div>

          {/* Total Approved Agents */}
          <div className="bg-green-600 text-white rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm mb-1">Total Approved Agents</p>
                <p className="text-2xl font-bold">
                  {approvalStatsDto?.totalApprovedAgents !== undefined ? approvalStatsDto.totalApprovedAgents : 'Loading...'}
                </p>
                {approvalStatsDto?.totalApprovedAgents === 0 && (
                  <p className="text-xs text-green-200">No approved agents</p>
                )}
              </div>
              <i className="fas fa-user-check text-green-200 text-2xl"></i>
            </div>
          </div>

          {/* Total Disapproved Agents */}
          <div className="bg-red-600 text-white rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-sm mb-1">Total DisApproved Agent</p>
                <p className="text-2xl font-bold">
                  {approvalStatsDto?.totalDisapprovedAgents !== undefined ? approvalStatsDto.totalDisapprovedAgents : 'Loading...'}
                </p>
                {approvalStatsDto?.totalDisapprovedAgents === 0 && (
                  <p className="text-xs text-red-200">No disapproved agents</p>
                )}
              </div>
              <i className="fas fa-user-times text-red-200 text-2xl"></i>
            </div>
          </div>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Today's Reports */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-4 py-3 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  Today's Reports: {todaysReport?.todayDate ? formatDate(todaysReport.todayDate) : 'Loading...'}
                </h3>
              </div>
              <div className="p-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                    <div className="flex items-center">
                      <i className="fas fa-plus-circle text-blue-600 mr-3"></i>
                      <span className="text-sm font-medium text-gray-700">Today's Total Generated Tickets</span>
                    </div>
                    <span className="text-lg font-bold text-blue-600">
                      {todaysReport?.totalGeneratedTickets !== undefined ? todaysReport.totalGeneratedTickets : 'Loading...'}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                    <div className="flex items-center">
                      <i className="fas fa-user-tag text-purple-600 mr-3"></i>
                      <span className="text-sm font-medium text-gray-700">Today's Total Assigned Tickets</span>
                    </div>
                    <span className="text-lg font-bold text-purple-600">
                      {todaysReport?.totalAssignedTickets !== undefined ? todaysReport.totalAssignedTickets : 'Loading...'}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                    <div className="flex items-center">
                      <i className="fas fa-check-double text-green-600 mr-3"></i>
                      <span className="text-sm font-medium text-gray-700">Today's Total Resolve Tickets</span>
                    </div>
                    <span className="text-lg font-bold text-green-600">
                      {todaysReport?.totalResolvedTickets !== undefined ? todaysReport.totalResolvedTickets : 'Loading...'}
                    </span>
                  </div>
                </div>

              </div>
            </div>
          </div>

          {/* Monthly Tickets Summary */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-4 py-3 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Monthly Tickets Summary</h3>
              </div>
              <div className="p-4">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Month
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Total Assigned Tickets
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Total Resolved Tickets
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {monthlyReport && monthlyReport.length > 0 ? (
                        monthlyReport.map((report, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                              {report.month}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                              {report.totalAssigned}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                              {report.totalResolved}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="3" className="px-4 py-8 text-center text-gray-500">
                            No monthly data available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="bg-red-600 text-white px-6 py-4 rounded-t-lg">
              <h3 className="text-lg font-semibold">Change Password Required</h3>
            </div>
            <div className="p-6">
              <p className="text-gray-600 mb-4">
                For security reasons, you must update your password before continuing.
              </p>
              <form onSubmit={handlePasswordSubmit}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword.newPassword ? 'text' : 'password'}
                      value={passwordForm.newPassword}
                      onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent pr-10"
                      required
                      minLength="8"
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('newPassword')}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      <i className={`fas ${showPassword.newPassword ? 'fa-eye-slash' : 'fa-eye'} text-gray-400`}></i>
                    </button>
                  </div>
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword.confirmPassword ? 'text' : 'password'}
                      value={passwordForm.confirmPassword}
                      onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent pr-10"
                      required
                      minLength="8"
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('confirmPassword')}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      <i className={`fas ${showPassword.confirmPassword ? 'fa-eye-slash' : 'fa-eye'} text-gray-400`}></i>
                    </button>
                  </div>
                </div>
                
                {passwordError && (
                  <div className="mb-4 text-red-600 text-sm">
                    {passwordError}
                  </div>
                )}
                
                <div className="flex justify-end">
                  <button
                    type="submit"
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md font-medium transition-colors"
                  >
                    Update Password
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportDashboard;